import { Text, View, TouchableOpacity, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from '@/components/People/Avatar';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import NotificationBadge from '@/components/General/NotificationBadge';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { isToday } from '@/utils/dater';
import { deleteConversation, toggleConversationPin } from '@/data/connect';
import SectionTitle from '@/components/General/SectionTitle';
import { showAlert } from '@/utils/showAlert';
import { useRouter, useNavigation, useFocusEffect } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';

export default function ConversationsScreen() {
    const router = useRouter();
    const navigation = useNavigation();

    const queryClient = useQueryClient();
    const { data: loggedInUser, isPending: loggedInUserLoading } = Query.people.loggedInUser()
    const { conversationUnread } = useNotificationBadges();

    //Redirect in an effect (never during render) and only once the user query has
    //settled — on cold start loggedInUser is undefined while the cache hydrates,
    //which would otherwise pop a deep-linked navigation straight back to home.
    useEffect(() => {
        if(loggedInUserLoading) return;
        if(!loggedInUser?.can?.("View Conversations")){
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loggedInUserLoading, loggedInUser]);

    const { data: conversations, isFetching: loadingConversations } = Query.conversations.byUserId({userId: loggedInUser?.id})

    const togglePinMutation = useMutation({
        mutationFn: toggleConversationPin,
        onSuccess: (data) => {
            queryClient.setQueryData(['conversations', {userId: loggedInUser?.id}], old => {
                return old.map(c => {
                    return (c.id == data.conversationID) ? {...c, pinnedForUser: data.isPinned} : c
                })
            })
        }
    })
    const leaveConversationMutation = useMutation({
        mutationFn: deleteConversation,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)})
        }
    })


    useFocusEffect(useCallback(() => {
        queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)});
    }, [queryClient, loggedInUser?.id]));


    useEffect(() => {
        navigation.setOptions({
            headerRight: !loggedInUser?.can?.("Create Conversations") ? null : () => (
                <View style={styles.headerRightContainer}>
                    <TouchableOpacity onPress={() => router.push("/connect/conversations/create")}>
                        <Unicon name="add" size={28} colorVariant="text"/>
                    </TouchableOpacity>
                </View>
            ),
        });
    }, [navigation])

    
    const handleConversationPress = (conversation) => {
        router.push(`/connect/conversations/${conversation.id}`);
    };


    const generateConversationName = (people) => {
        if(people?.length == 0){
            return "Nobody";
        }

        const peopleWithoutMe = people.filter(p => p.id != loggedInUser?.id);

        if(peopleWithoutMe?.length < 4){
            if(peopleWithoutMe?.length == 1){
                return peopleWithoutMe[0]?.firstName + " " + peopleWithoutMe[0]?.lastName
            }

            return peopleWithoutMe.map((p, i) => {
                return i == 0 
                    ? p.firstName
                    : i == peopleWithoutMe?.length - 1
                        ? " & " + p.firstName
                        : ", " + p.firstName;
            })
        }
        else{
            return peopleWithoutMe[0].firstName + ", " + peopleWithoutMe[1].firstName + " & " + (peopleWithoutMe?.length - 2) + " others";
        }
    }

    const RenderItem = ({ item }) => {
        if (typeof item === "string") {
            // Rendering header
            if(conversations.filter(c => c.pinnedForUser).length == 0) return null
            return <SectionTitle title={item} containerStyle={{marginLeft:15, marginTop:item == "Everything Else" ? 40 : 20}} />;
        }

        const unreadCount = conversationUnread(item.id);

        const avatarProfile = item?.avatarPath ? {id: item?.id, key: item?.avatarPath} : (item?.lastMessage && item?.lastMessage?.Sender?.id != loggedInUser?.id)
            ? item?.lastMessage?.Sender 
            : item?.People[0]?.id != loggedInUser?.id
                ? item?.People[0]
                : item?.People[1]

        return (
            <View style={styles.conversationContainer}>
                <Pressable style={styles.conversationPressable} onPress={() => handleConversationPress(item)}>
                    <NotificationBadge count={unreadCount} size={20} style={styles.badgeContainer} testID={`conversation-badge-${item.id}`} />
                    <View style={styles.avatarContainer}>
                        <Avatar disableBorder size={40} profile={avatarProfile} directory={item?.avatarPath ? "conversations/" : "avatars/"}/>
                    </View>
                    <View style={styles.conversationTextContainer}>
                        <View style={styles.nameRow}>
                            <Text style={styles.name} numberOfLines={1}>{item?.name === "*NONE*" ? generateConversationName(item?.People) : item?.name}</Text>
                            {item?.mutedForUser && (
                                <Unicon name="notifications-off-outline" size={14} colorVariant="textSecondary"/>
                            )}
                        </View>
                        <Text numberOfLines={2} style={styles.message}>
                            { !item?.lastMessage ? "No messages yet" : (
                                <>
                                    <Text style={styles.senderPrefix}>{item?.lastMessage?.Sender?.id == loggedInUser?.id ? "Me" : item?.lastMessage?.Sender?.firstName}: </Text>
                                    <Text>{item?.lastMessage?.text}</Text>
                                </>
                            )}
                        </Text>
                    </View>
                </Pressable>
                <View style={styles.rightContainer}>
                    <View style={styles.dropdownContainer}>
                        <DropdownMenu options={[
                            {name: (item?.pinnedForUser) ? 'Unpin Conversation' : 'Pin Conversation', icon: (item?.pinnedForUser) ? dropdownIcons.unpin : dropdownIcons.pin, onSelect: () => {
                                setTimeout(() => {
                                    togglePinMutation.mutate({conversationID: item?.id, personID: loggedInUser?.id, isPinned: !item?.pinnedForUser});
                                }, 250)
                            }},
                            ...(item?.People?.length > 2 ? [{name: 'Leave Conversation', destructive: true, icon: {android: "logout", ios: "square.and.arrow.up", web: "log-out"}, onSelect: () => {
                                showAlert({
                                    title: "Leave Conversation",
                                    subtitle: "This will remove you from the conversation but will not delete it for others in the conversation, and of your previous messages will be visible to others who are still in the conversation.",
                                    isDestructive: true,
                                    onConfirm: () => leaveConversationMutation.mutate({conversationID: item?.id, personID: loggedInUser?.id}),
                                    confirmText: "Leave"
                                })
                            }}] : [])
                        ]}/>
                    </View>
                    <View>
                        <Text style={styles.time}>{item?.lastMessage
                            ? new Date(item?.lastMessage?.sentAt).toLocaleString('en-US', isToday(new Date(item?.lastMessage?.sentAt)) ? { hour:"numeric", minute:"2-digit"} : { month:"numeric", day:"numeric", year:"numeric"} ) 
                            : new Date(item?.createdAt).toLocaleString('en-US', isToday(new Date(item?.createdAt)) ? { hour:"numeric", minute:"2-digit"} : { month:"numeric", day:"2-digit", year:"2-digit"})}
                        </Text>
                    </View>
                </View>
            </View>
        )
    }

    
    const styles = StyleSheet.create(({colors, fonts}) => ({
        conversationContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: 10,
            paddingLeft:10,
            borderBottomWidth: 1,
            borderBottomColor: colors.border
        },
        conversationTextContainer: {
            flex: 1,
            justifyContent: 'center',
        },
        nameRow: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
        },
        name: {
            fontSize: 15,
            fontWeight: '500',
            fontFamily:fonts.bold,
            color:colors.text
        },
        message: {
            fontSize: 12,
            color: colors.textSecondary,
            fontFamily:fonts.heading,
            marginTop: 6,
        },
        time: {
            fontSize: 12,
            color: colors.textSecondary,
            fontFamily:fonts.heading,
            marginLeft: 10,
        },
        headerRightContainer: {
            flexDirection:"row"
        },
        conversationPressable: {
            flex:1,
            flexDirection:"row",
            alignItems:"center"
        },
        badgeContainer: {
            position:"absolute",
            top:-7,
            left:0,
            zIndex:1
        },
        avatarContainer: {
            marginLeft:5,
            marginRight:15
        },
        senderPrefix: {
            color:colors.text
        },
        rightContainer: {
            alignItems:"flex-end",
            justifyContent:"space-between",
            marginRight:10
        },
        dropdownContainer: {
            marginBottom:5
        },
        contentLoaderRow: {
            flexDirection:"row",
            justifyContent:"space-between",
            marginVertical:4
        },
        contentLoaderDetail: {
            marginHorizontal:10,
            flex:1,
            justifyContent:"center"
        },
        contentLoaderLine: {
            marginVertical:3
        },
        contentLoaderTimestamp: {
            position:"absolute",
            top:5,
            right:-10
        },
        container: {
            flex:1
        },
        listFooter: {
            paddingBottom:80
        },
        listFooterLoading: {
            marginHorizontal:5
        },
    }));

    let contentLoaders = [];

    if(loadingConversations){
        for(let i=0; i<10; i++){
            contentLoaders.push(<View key={"loaders" + i} style={styles.contentLoaderRow}>
                <ContentLoader containerHeight={40} containerWidth={40}  borderRadius={40} staggerPosition={i}/>
                <View style={styles.contentLoaderDetail}>
                    <ContentLoader containerHeight={12} containerWidth={10 + Math.random()*40 + "%"} staggerPosition={i} style={styles.contentLoaderLine}/>
                    <ContentLoader containerHeight={12} containerWidth={10 + Math.random()*70 + "%"} staggerPosition={i+1} style={styles.contentLoaderLine}/>
                    <ContentLoader containerHeight={12} containerWidth={50} staggerPosition={i+1} style={styles.contentLoaderTimestamp}/>
                </View>
            </View>)
        }
    }

    return (
        <View style={styles.container}>
            <LegendList recycleItems keyExtractor={(item) => item.id || item} 
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={80}
                data={loadingConversations ? [] : [
                    "Pinned",
                    ...conversations.filter(c => c.pinnedForUser),
                    "Everything Else",
                    ...conversations.filter(c => !c.pinnedForUser),
                ]}
                renderItem={({item}) => <RenderItem item={item} />}
                progressViewOffset={1000}
                refreshing={false}
                onRefresh={() => {
                    queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)})
                }}
                getItemType={(item) => {
                    return typeof item === "string" ? "sectionHeader" : "row";
                }}
                ListFooterComponent={!loadingConversations
                    ? <View style={styles.listFooter}>{
                        conversations?.length == 0 ? <EmptyListPlaceholder icon="chatbox-ellipses-outline" spacing={10} message="*Crickets..* Start a new conversation!"/> 
                        : <View></View>
                    }
                    </View> 
                    : <View style={styles.listFooterLoading}>
                        {contentLoaders}
                    </View>}
            />
        </View>
       
    )
}